import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Bell, Plus, Send, Edit2, Trash2, Users, Filter, Search,
  FileText, Link as LinkIcon, ExternalLink, Upload, X, Eye,
  AlertTriangle, Calendar, Receipt, Heart, Megaphone, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category definitions
const CATEGORIES = [
  { value: 'info', label: 'Information', icon: Info, color: 'bg-blue-100 text-blue-800' },
  { value: 'cours', label: 'Cours / Stage', icon: Calendar, color: 'bg-green-100 text-green-800' },
  { value: 'cours_stages', label: 'Cours / Stage', icon: Calendar, color: 'bg-green-100 text-green-800' },
  { value: 'annulation', label: 'Annulation', icon: X, color: 'bg-red-100 text-red-800' },
  { value: 'facturation', label: 'Facturation', icon: Receipt, color: 'bg-amber-100 text-amber-800' },
  { value: 'tarifs', label: 'Tarifs', icon: Receipt, color: 'bg-amber-100 text-amber-800' },
  { value: 'soins', label: 'Soins cheval', icon: Heart, color: 'bg-emerald-100 text-emerald-800' },
  { value: 'chevaux', label: 'Chevaux', icon: Heart, color: 'bg-emerald-100 text-emerald-800' },
  { value: 'groupes', label: 'Groupes', icon: Users, color: 'bg-indigo-100 text-indigo-800' },
  { value: 'vie_club', label: 'Vie du club', icon: Megaphone, color: 'bg-purple-100 text-purple-800' },
  { value: 'urgent', label: 'Urgent', icon: AlertTriangle, color: 'bg-red-100 text-red-800' },
];

export const NotificationsAdmin = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    contenu: '',
    category: 'info',
    type: 'general',
    targeting: { all_riders: true },
    links: [],
    attachment_path: null,
    attachment_name: null,
    send_email: false
  });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  
  // Targeting preview
  const [targetingPreview, setTargetingPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Groups and horses for targeting
  const [groups, setGroups] = useState([]);
  const [horses, setHorses] = useState([]);
  
  // Link editor
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [newLink, setNewLink] = useState({ type: 'internal', url: '', label: '' });

  // Check access
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      toast.error('Accès réservé aux administrateurs');
    }
  }, [isAdmin, navigate]);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/notifications?${params}`);
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchGroupsAndHorses = useCallback(async () => {
    try {
      const [groupsRes, horsesRes] = await Promise.all([
        axios.get(`${API_URL}/api/groupes`),
        axios.get(`${API_URL}/api/horses`)
      ]);
      setGroups(groupsRes.data || []);
      setHorses(horsesRes.data || []);
    } catch (error) {
      console.error('Error fetching groups/horses:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchGroupsAndHorses();
  }, [fetchNotifications, fetchGroupsAndHorses]);

  // Check for context from URL (when coming from "Share" button)
  useEffect(() => {
    const contextType = searchParams.get('context_type');
    const contextId = searchParams.get('context_id');
    const titre = searchParams.get('titre');
    
    if (contextType) {
      setFormData(prev => ({
        ...prev,
        context_type: contextType,
        context_id: contextId,
        titre: titre || prev.titre
      }));
      setEditorOpen(true);
    }
  }, [searchParams]);

  const fetchTargetingPreview = async () => {
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams();
      if (formData.targeting.categorie_age) params.append('categorie_age', formData.targeting.categorie_age);
      if (formData.targeting.statuts?.length) params.append('statuts', formData.targeting.statuts.join(','));
      if (formData.targeting.groupe_ids?.length) params.append('groupe_ids', formData.targeting.groupe_ids.join(','));
      if (formData.targeting.cheval_id) params.append('cheval_id', formData.targeting.cheval_id);
      
      const response = await axios.get(`${API_URL}/api/admin/notifications/targeting/preview?${params}`);
      setTargetingPreview(response.data);
    } catch (error) {
      console.error('Error fetching targeting preview:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingNotif(null);
    setFormData({
      titre: '',
      contenu: '',
      category: 'info',
      type: 'general',
      targeting: { all_riders: true },
      links: [],
      attachment_path: null,
      attachment_name: null,
      send_email: false
    });
    setTargetingPreview(null);
    setEditorOpen(true);
  };

  const handleEdit = (notif) => {
    setEditingNotif(notif);
    setFormData({
      titre: notif.titre,
      contenu: notif.contenu,
      category: notif.category,
      type: notif.type,
      targeting: notif.targeting || { all_riders: true },
      links: notif.links || [],
      attachment_path: notif.attachment_path,
      attachment_name: notif.attachment_name,
      send_email: false
    });
    setTargetingPreview(null);
    setEditorOpen(true);
  };

  const handleSave = async (publish = false) => {
    if (!formData.titre.trim() || !formData.contenu.trim()) {
      toast.error('Titre et contenu sont obligatoires');
      return;
    }

    if (publish) {
      setPublishing(true);
    } else {
      setSaving(true);
    }

    try {
      if (editingNotif) {
        // Update existing
        await axios.put(`${API_URL}/api/admin/notifications/${editingNotif.id}`, formData);
        
        if (publish) {
          // Check if already published - use republish endpoint instead
          if (editingNotif.status === 'published') {
            await axios.post(`${API_URL}/api/admin/notifications/${editingNotif.id}/republish?send_email=${formData.send_email}`);
            toast.success('Notification mise à jour et republiée !');
          } else {
            await axios.post(`${API_URL}/api/admin/notifications/${editingNotif.id}/publish?send_email=${formData.send_email}`);
            toast.success('Notification publiée !');
          }
        } else {
          toast.success('Notification mise à jour');
        }
      } else {
        // Create new
        const response = await axios.post(`${API_URL}/api/admin/notifications`, formData);
        
        if (publish) {
          await axios.post(`${API_URL}/api/admin/notifications/${response.data.notification.id}/publish?send_email=${formData.send_email}`);
          toast.success('Notification créée et publiée !');
        } else {
          toast.success('Brouillon enregistré');
        }
      }
      
      setEditorOpen(false);
      fetchNotifications();
    } catch (error) {
      console.error('Error saving notification:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Erreur lors de l\'enregistrement');
      toast.error(errorMessage);
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  const handleDelete = async (notifId) => {
    const notif = notifications.find(n => n.id === notifId);
    const confirmMsg = notif?.status === 'published' 
      ? 'Supprimer cette notification ? Elle sera également supprimée pour tous les destinataires.'
      : 'Supprimer ce brouillon ?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/notifications/${notifId}`);
      toast.success('Notification supprimée');
      fetchNotifications();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Erreur lors de la suppression');
      toast.error(errorMessage);
    }
  };

  const handleRepublish = async (notifId) => {
    if (!window.confirm('Republier cette notification ? Elle sera renvoyée à tous les destinataires ciblés.')) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/notifications/${notifId}/republish`);
      toast.success(`Notification republiée à ${response.data.recipients_count} destinataire(s)`);
      fetchNotifications();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects) or string errors
      const errorMessage = Array.isArray(errorDetail) 
        ? errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (typeof errorDetail === 'string' ? errorDetail : 'Erreur lors de la republication');
      toast.error(errorMessage);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont autorisés');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5 Mo)');
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/admin/notifications/upload-attachment`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({
        ...prev,
        attachment_path: response.data.path,
        attachment_name: response.data.name
      }));
      toast.success('Fichier uploadé');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    }
  };

  const addLink = () => {
    if (!newLink.url.trim()) {
      toast.error('URL requise');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      links: [...prev.links, { ...newLink }]
    }));
    setNewLink({ type: 'internal', url: '', label: '' });
    setLinkEditorOpen(false);
  };

  const removeLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  const getCategoryInfo = (categoryValue) => {
    return CATEGORIES.find(c => c.value === categoryValue) || CATEGORIES[0];
  };

  const filteredNotifications = notifications.filter(n => 
    (n.titre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.contenu.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="notifications-admin">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D] flex items-center gap-2">
            <Bell className="w-7 h-7" />
            Notifications
          </h1>
          <p className="text-stone-500 mt-1">Créer et gérer les notifications du club</p>
        </div>
        <Button 
          onClick={handleCreate}
          className="btn-primary"
          data-testid="create-notification-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle notification
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="draft">Brouillons</SelectItem>
                <SelectItem value="published">Publiées</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Notifications list */}
      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">Aucune notification</p>
              <Button onClick={handleCreate} variant="link" className="mt-2">
                Créer une notification
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredNotifications.map((notif) => {
            const categoryInfo = getCategoryInfo(notif.category);
            const CategoryIcon = categoryInfo.icon;
            
            return (
              <Card key={notif.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={categoryInfo.color}>
                          <CategoryIcon className="w-3 h-3 mr-1" />
                          {categoryInfo.label}
                        </Badge>
                        <Badge variant={notif.status === 'published' ? 'default' : 'outline'}>
                          {notif.status === 'published' ? 'Publiée' : 'Brouillon'}
                        </Badge>
                      </div>
                      
                      <h3 className="font-semibold text-stone-800 mb-1">{notif.titre}</h3>
                      <p className="text-sm text-stone-600 line-clamp-2" 
                         dangerouslySetInnerHTML={{ __html: notif.contenu }} />
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                        <span>
                          Créée le {format(new Date(notif.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                        </span>
                        {notif.status === 'published' && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {notif.recipients_count} destinataire(s)
                            </span>
                            {notif.email_sent_count > 0 && (
                              <>
                                <span>•</span>
                                <span>{notif.email_sent_count} email(s)</span>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {notif.status === 'draft' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(notif)}
                            data-testid={`edit-notif-${notif.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(notif.id)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`delete-notif-${notif.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {notif.status === 'published' && (notif.author_id === user?.id || notif.created_by === user?.id) && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(notif)}
                            title="Modifier la notification"
                            data-testid={`edit-notif-${notif.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRepublish(notif.id)}
                            className="text-blue-600 hover:bg-blue-50"
                            title="Republier la notification"
                            data-testid={`republish-notif-${notif.id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(notif.id)}
                            className="text-red-600 hover:bg-red-50"
                            title="Supprimer la notification"
                            data-testid={`delete-notif-${notif.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {notif.status === 'published' && notif.author_id !== user?.id && notif.created_by !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(notif)}
                          title="Voir la notification"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingNotif ? 'Modifier la notification' : 'Nouvelle notification'}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="content" className="mt-4">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="content">Contenu</TabsTrigger>
              <TabsTrigger value="targeting">Ciblage</TabsTrigger>
              <TabsTrigger value="options">Options</TabsTrigger>
            </TabsList>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-4 mt-4">
              <div>
                <Label>Catégorie</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => {
                      const Icon = cat.icon;
                      return (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {cat.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Titre *</Label>
                <Input
                  value={formData.titre}
                  onChange={(e) => setFormData(p => ({ ...p, titre: e.target.value }))}
                  placeholder="Titre de la notification"
                  maxLength={200}
                />
              </div>

              <div>
                <Label>Contenu *</Label>
                <Textarea
                  value={formData.contenu}
                  onChange={(e) => setFormData(p => ({ ...p, contenu: e.target.value }))}
                  placeholder="Contenu de la notification (HTML supporté pour gras, italique, liens)"
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Mise en forme : &lt;b&gt;gras&lt;/b&gt;, &lt;i&gt;italique&lt;/i&gt;, &lt;a href=&quot;...&quot;&gt;lien&lt;/a&gt;
                </p>
              </div>

              {/* Links */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Liens</Label>
                  <Button variant="outline" size="sm" onClick={() => setLinkEditorOpen(true)}>
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter
                  </Button>
                </div>
                {formData.links.length > 0 ? (
                  <div className="space-y-2">
                    {formData.links.map((link, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-stone-50 rounded">
                        {link.type === 'external' ? (
                          <ExternalLink className="w-4 h-4 text-stone-400" />
                        ) : (
                          <LinkIcon className="w-4 h-4 text-stone-400" />
                        )}
                        <span className="flex-1 text-sm truncate">{link.label || link.url}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeLink(idx)}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-stone-500">Aucun lien</p>
                )}
              </div>

              {/* Attachment */}
              <div>
                <Label>Pièce jointe (PDF)</Label>
                {formData.attachment_path ? (
                  <div className="flex items-center gap-2 p-2 bg-stone-50 rounded mt-1">
                    <FileText className="w-4 h-4 text-stone-400" />
                    <span className="flex-1 text-sm truncate">{formData.attachment_name}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setFormData(p => ({ ...p, attachment_path: null, attachment_name: null }))}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="mt-1">
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-stone-300 rounded-lg p-4 text-center hover:border-stone-400 transition-colors">
                        <Upload className="w-6 h-6 mx-auto text-stone-400 mb-2" />
                        <p className="text-sm text-stone-600">Cliquez pour uploader un PDF</p>
                        <p className="text-xs text-stone-400">Max 5 Mo</p>
                      </div>
                      <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" />
                    </label>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Targeting Tab */}
            <TabsContent value="targeting" className="space-y-4 mt-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.targeting.all_riders}
                  onCheckedChange={(checked) => setFormData(p => ({
                    ...p,
                    targeting: { ...p.targeting, all_riders: checked }
                  }))}
                />
                <Label>Tous les cavaliers</Label>
              </div>

              {!formData.targeting.all_riders && (
                <>
                  {/* Age category filter */}
                  <div>
                    <Label>Catégorie d'âge</Label>
                    <Select
                      value={formData.targeting.categorie_age || 'all'}
                      onValueChange={(v) => setFormData(p => ({
                        ...p,
                        targeting: { ...p.targeting, categorie_age: v === 'all' ? null : v }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Toutes les catégories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les catégories</SelectItem>
                        <SelectItem value="adultes">Adultes</SelectItem>
                        <SelectItem value="ados">Ados</SelectItem>
                        <SelectItem value="enfants">Enfants</SelectItem>
                        <SelectItem value="tout_petits">Tout petits</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-stone-500 mt-1">
                      Cible les cavaliers selon leur catégorie d'âge
                    </p>
                  </div>

                  {/* Group filter */}
                  {groups.length > 0 && (
                    <div>
                      <Label>Groupes pédagogiques</Label>
                      <Select
                        value={formData.targeting.groupe_ids?.[0] || 'all'}
                        onValueChange={(v) => setFormData(p => ({
                          ...p,
                          targeting: { ...p.targeting, groupe_ids: v === 'all' ? null : [v] }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Tous les groupes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les groupes</SelectItem>
                          {groups.map(g => (
                            <SelectItem key={g.id} value={g.id}>{g.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Horse filter */}
                  {horses.length > 0 && (
                    <div>
                      <Label>Cavaliers liés à un cheval</Label>
                      <Select
                        value={formData.targeting.cheval_id || 'all'}
                        onValueChange={(v) => setFormData(p => ({
                          ...p,
                          targeting: { ...p.targeting, cheval_id: v === 'all' ? null : v }
                        }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Tous les chevaux" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les chevaux</SelectItem>
                          {horses.map(h => (
                            <SelectItem key={h.id} value={h.id}>{h.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}

              {/* Preview button */}
              <Button
                variant="outline"
                onClick={fetchTargetingPreview}
                disabled={previewLoading}
                className="w-full"
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewLoading ? 'Chargement...' : 'Prévisualiser les destinataires'}
              </Button>

              {targetingPreview && (
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="font-medium text-stone-800">
                    {targetingPreview.count} destinataire(s) ciblé(s)
                  </p>
                  {targetingPreview.recipients.length > 0 && (
                    <div className="mt-2 text-sm text-stone-600">
                      {targetingPreview.recipients.slice(0, 5).map(r => (
                        <span key={r.id} className="inline-block bg-white px-2 py-1 rounded mr-1 mb-1">
                          {r.prenom} {r.nom}
                        </span>
                      ))}
                      {targetingPreview.count > 5 && (
                        <span className="text-stone-400">
                          et {targetingPreview.count - 5} autre(s)...
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Options Tab */}
            <TabsContent value="options" className="space-y-4 mt-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.send_email}
                  onCheckedChange={(checked) => setFormData(p => ({ ...p, send_email: checked }))}
                />
                <div>
                  <Label>Envoyer aussi par email</Label>
                  <p className="text-xs text-stone-500">
                    Un email sera envoyé à tous les destinataires ayant activé les notifications email
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Annuler
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleSave(false)}
              disabled={saving || publishing}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer brouillon'}
            </Button>
            <Button 
              onClick={() => handleSave(true)}
              disabled={saving || publishing}
              className="btn-primary"
            >
              {publishing ? 'Publication...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Publier
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Editor Dialog */}
      <Dialog open={linkEditorOpen} onOpenChange={setLinkEditorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un lien</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Type</Label>
              <Select value={newLink.type} onValueChange={(v) => setNewLink(p => ({ ...p, type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Lien interne (Equibook)</SelectItem>
                  <SelectItem value="external">Lien externe (site web)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>URL</Label>
              <Input
                value={newLink.url}
                onChange={(e) => setNewLink(p => ({ ...p, url: e.target.value }))}
                placeholder={newLink.type === 'internal' ? '/planning?filter=stages' : 'https://...'}
              />
            </div>
            <div>
              <Label>Libellé (optionnel)</Label>
              <Input
                value={newLink.label}
                onChange={(e) => setNewLink(p => ({ ...p, label: e.target.value }))}
                placeholder="Voir les stages"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setLinkEditorOpen(false)}>Annuler</Button>
            <Button onClick={addLink}>Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsAdmin;
